import React from "react";
import type {
  BlockGridLayout,
  BlockGridItem,
  BlockGridArea,
} from "../../../types/blockGrid";
import { hasAreas } from "../../../types/blockGrid";
import styles from "./BlockGrid.module.css";

export interface BlockGridProps {
  /**
   * The block grid layout data from Umbraco
   */
  layout: BlockGridLayout;

  /**
   * Function to render a specific block type
   * Should return the appropriate component for the block's content type
   */
  renderBlock: (
    item: BlockGridItem,
    areaChildren?: React.ReactNode
  ) => React.ReactNode;

  /**
   * Optional CSS class name for the grid container
   */
  className?: string;
}

/**
 * BlockGrid component that renders a 12-column CSS Grid layout
 * based on Umbraco Block Grid data
 */
export const BlockGrid: React.FC<BlockGridProps> = ({
  layout,
  renderBlock,
  className,
}) => {
  const { gridColumns, items } = layout;

  const containerClasses = [styles.blockGrid, className ?? ""]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={containerClasses}
      style={{ "--grid-columns": gridColumns } as React.CSSProperties}
      data-component="block-grid"
    >
      <div className={styles.gridContainer}>
        {items.map((item) => (
          <BlockGridItemComponent
            key={item.id}
            item={item}
            renderBlock={renderBlock}
          />
        ))}
      </div>
    </div>
  );
};

interface BlockGridItemProps {
  item: BlockGridItem;
  renderBlock: (
    item: BlockGridItem,
    areaChildren?: React.ReactNode
  ) => React.ReactNode;
}

/**
 * Individual block grid item with column/row span styling
 */
const BlockGridItemComponent: React.FC<BlockGridItemProps> = ({
  item,
  renderBlock,
}) => {
  const { columnSpan, rowSpan } = item;
  const isLayoutBlock = hasAreas(item);

  // The column span goes in as a custom property the stylesheet reads, not as
  // an inline grid-column: an inline style beats the stylesheet, so the tablet
  // rules that map spans onto 8 columns never applied, and a span-12 block
  // added four empty columns past the 8 that narrowed the other blocks.
  const itemStyle = {
    "--column-span": columnSpan,
    gridRow: `span ${rowSpan}`,
  } as React.CSSProperties;

  // Build the areas content (used by both layout and regular blocks)
  const areasContent = isLayoutBlock ? (
    <div className={styles.areasContainer}>
      {item.areas.map((area) => (
        <BlockGridAreaComponent
          key={area.alias}
          area={area}
          renderBlock={renderBlock}
        />
      ))}
    </div>
  ) : null;

  return (
    <div
      className={styles.gridItem}
      style={itemStyle}
      data-block-type={item.type}
      data-column-span={columnSpan}
      data-row-span={rowSpan}
    >
      {/*
       * Layout blocks (blocks with areas): pass area children through
       * renderBlock so the component can wrap them (e.g., StyledRow).
       * Regular blocks: render content normally, then areas separately.
       */}
      {item.content &&
        renderBlock(item, isLayoutBlock ? areasContent : undefined)}
      {!isLayoutBlock && areasContent}
    </div>
  );
};

interface BlockGridAreaProps {
  area: BlockGridArea;
  renderBlock: (
    item: BlockGridItem,
    areaChildren?: React.ReactNode
  ) => React.ReactNode;
}

/**
 * Block grid area component for nested layouts
 */
const BlockGridAreaComponent: React.FC<BlockGridAreaProps> = ({
  area,
  renderBlock,
}) => {
  const areaStyle: React.CSSProperties = {
    gridColumn: `span ${area.columnSpan}`,
    gridRow: `span ${area.rowSpan}`,
  };

  return (
    <div
      className={styles.gridArea}
      style={areaStyle}
      data-area-alias={area.alias}
    >
      <div className={styles.areaGrid}>
        {area.items.map((item) => (
          <BlockGridItemComponent
            key={item.id}
            item={item}
            renderBlock={renderBlock}
          />
        ))}
      </div>
    </div>
  );
};

export default BlockGrid;
